// Camera feel metrics (the feel-gates suggestion in engine/docs/research/suggestions/harness-qa-and-agent-workflow.md):
// jitter measured through a runtime's real frame path with scripted frame times. A game drives frames at a pattern
// (60 Hz, 144 Hz, 60 Hz ± 2 ms), records the followed target's screen position and the camera's position each frame,
// and these turn the records into numbers. Headless runs give these numbers exactly: they come from the frame
// pattern, not from the machine's real frame times.

/** A frame-time pattern: "60" and "144" are steady; "noisy" is 60 Hz ± 2 ms from a seeded generator. */
export type FramePattern = "60" | "144" | "noisy";

/** `n` frame times (seconds) for a pattern. The noisy pattern is seeded, so a run repeats. */
export function frameTimes(pattern: FramePattern, n: number, seed = 1): number[] {
  if (pattern === "60") return Array.from({ length: n }, () => 1 / 60);
  if (pattern === "144") return Array.from({ length: n }, () => 1 / 144);
  let a = seed >>> 0 || 1;
  const rnd = (): number => {
    // xorshift32
    a ^= a << 13;
    a >>>= 0;
    a ^= a >>> 17;
    a ^= a << 5;
    a >>>= 0;
    return a / 4294967296;
  };
  return Array.from({ length: n }, () => 1 / 60 + (rnd() * 2 - 1) * 0.002);
}

/** RMS of a series around its best straight line (what's left after a steady drift is removed). */
export function detrendedRms(ys: readonly number[]): number {
  const n = ys.length;
  if (n < 3) return 0;
  const xm = (n - 1) / 2;
  const ym = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0;
  let sxx = 0;
  ys.forEach((y, i) => {
    sxy += (i - xm) * (y - ym);
    sxx += (i - xm) ** 2;
  });
  const b = sxy / sxx;
  return Math.sqrt(ys.reduce((acc, y, i) => acc + (y - (ym + b * (i - xm))) ** 2, 0) / n);
}

/** RMS of the second difference per frame (frame-to-frame changes in screen motion). */
export function secondDifferenceRms(ys: readonly number[]): number {
  if (ys.length < 3) return 0;
  let s = 0;
  for (let i = 2; i < ys.length; i++) s += (ys[i]! - 2 * ys[i - 1]! + ys[i - 2]!) ** 2;
  return Math.sqrt(s / (ys.length - 2));
}

/** One recorded frame: the target's screen position (px) and the camera's world position, with the frame's dt. */
export interface JitterSample {
  dt: number;
  screen: { x: number; y: number };
  camera: { x: number; y: number; z: number };
}

/** The jitter numbers for a steady move (straight run, view held). */
export interface JitterReport {
  /** The target's screen jitter: RMS of its screen position around a straight line (px). */
  px: number;
  /** RMS of the screen position's second difference (px per frame²): the step-to-step judder. */
  px2: number;
  /** The camera's world-speed spread: RMS of the speed over its mean (%), so the background slides evenly. */
  judder: number;
  frames: number;
}

/** Turn recorded frames into jitter numbers. Leave warm-up frames out before calling. */
export function jitterOf(samples: readonly JitterSample[]): JitterReport {
  const xs = samples.map((s) => s.screen.x);
  const ys = samples.map((s) => s.screen.y);
  const speeds: number[] = [];
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1]!.camera;
    const b = samples[i]!.camera;
    const dt = samples[i]!.dt;
    if (dt > 0) speeds.push(Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) / dt);
  }
  const mean = speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;
  const spread = mean > 1e-9 ? (Math.sqrt(speeds.reduce((a, s) => a + (s - mean) ** 2, 0) / speeds.length) / mean) * 100 : 0;
  return { px: Math.hypot(detrendedRms(xs), detrendedRms(ys)), px2: Math.hypot(secondDifferenceRms(xs), secondDifferenceRms(ys)), judder: spread, frames: samples.length };
}
